import * as React from 'react'
import { debounce } from '@/lib/debounce'
import { Megaphone } from 'lucide-react'
import { Banner, bannerService } from '@/services/bannerService'
import { BannerTable } from '@/components/banner/BannerTable'
import { BannerFilters } from '@/components/banner/BannerFilters'
import { BannerStats } from '@/components/banner/BannerStats'
import { useToast } from '@/components/ui/use-toast'
import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { useNavigate } from 'react-router-dom'

type FilterStatus = 'all' | 'active' | 'scheduled' | 'expired' | 'inactive';
type TargetFilter = 'all' | 'targeted' | 'global';
type DisplayTypeFilter = 'all' | 'banner' | 'news' | 'modal';
type BannerStatus = Exclude<FilterStatus, 'all'>;

const getBannerStatus = (banner: Banner): BannerStatus => {
  const now = new Date();
  const startDate = new Date(banner.startDate);
  const endDate = new Date(banner.endDate);

  if (!banner.active) return 'inactive';
  if (now < startDate) return 'scheduled';
  if (now > endDate) return 'expired';
  return 'active';
};

export default function BannerListPage() {
  const { toast } = useToast()
  const navigate = useNavigate()
  const [banners, setBanners] = React.useState<Banner[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [searchQuery, setSearchQuery] = React.useState('')
  const [filterStatus, setFilterStatus] = React.useState<FilterStatus>('all')
  const [targetFilter, setTargetFilter] = React.useState<TargetFilter>('all')
  const [displayTypeFilter, setDisplayTypeFilter] = React.useState<DisplayTypeFilter>('all')

  // Filtrelenmiş banner listesi
  const filteredBanners = React.useMemo(() => {
    return banners.filter(banner => {
      // Arama sorgusuna göre filtreleme
      const matchesSearch =
        banner.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        banner.content.toLowerCase().includes(searchQuery.toLowerCase())

      // Duruma göre filtreleme
      const status = getBannerStatus(banner);
      const matchesStatus =
        filterStatus === 'all' || status === filterStatus

      // Hedeflemeye göre filtreleme
      const isTargeted = banner.targetCompanies && banner.targetCompanies.length > 0;
      const matchesTarget =
        targetFilter === 'all' ||
        (targetFilter === 'targeted' && isTargeted) ||
        (targetFilter === 'global' && !isTargeted)

      // Tip filtreleme
      const matchesDisplayType =
        displayTypeFilter === 'all' ||
        (banner.displayType || 'banner') === displayTypeFilter

      return matchesSearch && matchesStatus && matchesTarget && matchesDisplayType
    })
  }, [banners, searchQuery, filterStatus, targetFilter, displayTypeFilter])

  // Debounced arama
  const debouncedSearch = React.useCallback(
    debounce((query: string) => {
      setSearchQuery(query)
    }, 300),
    []
  )

  // Bannerları getir
  const fetchBanners = React.useCallback(async () => {
    try {
      const response = await bannerService.getBanners(1, 1000) // Tüm bannerları getir
      setBanners(Array.isArray(response.banners) ? response.banners : [])
    } catch (error) {
      console.error('Bannerlar getirilirken hata:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Bannerlar getirilirken bir hata oluştu.'
      })
      setBanners([])
    } finally {
      setIsLoading(false)
    }
  }, [toast])

  // Banner sil
  const handleDeleteBanner = async (id: string) => {
    try {
      await bannerService.deleteBanner(id)
      await fetchBanners()
      toast({
        title: 'Başarılı',
        description: 'Banner başarıyla silindi.'
      })
    } catch (error) {
      console.error('Banner silinirken hata:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Banner silinirken bir hata oluştu.'
      })
    }
  }

  React.useEffect(() => {
    fetchBanners()
  }, [fetchBanners])

  if (isLoading) {
    return (
      <div className="h-full flex-1">
        <div className="flex-1 space-y-6 p-8 pt-6">
          {/* Hero Section Skeleton */}
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <div className="h-56 bg-gradient-to-r from-purple-800 to-purple-500 p-8">
              <div className="flex justify-between items-start relative w-full h-full">
                <div className="space-y-3 flex-1">
                  <Skeleton className="h-4 w-40 bg-white/20" />
                  <Skeleton className="h-9 w-80 bg-white/30" />
                  <Skeleton className="h-16 w-[500px] bg-white/20" />
                </div>
                <Skeleton className="w-40 h-40 rounded-lg bg-white/10" />
              </div>
            </div>
          </div>

          {/* BannerStats skeleton - Birinci satır kartları */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300 bg-white rounded-xl">
                <CardContent className="p-0">
                  <div className="relative">
                    <div className="absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full bg-slate-200 blur-2xl"></div>
                    <div className="p-6 relative">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <Skeleton className="h-4 w-[120px] mb-2" />
                          <Skeleton className="h-9 w-[70px]" />
                        </div>
                        <Skeleton className="h-14 w-14 rounded-xl" />
                      </div>
                      <div className="pt-3 border-t border-slate-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Skeleton className="w-2 h-2 rounded-full mr-2" />
                            <Skeleton className="h-4 w-[120px]" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* İkinci satır kartları */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300 bg-white rounded-xl">
                <CardContent className="p-0">
                  <div className="relative">
                    <div className="absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full bg-slate-200 blur-2xl"></div>
                    <div className="p-6 relative">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <Skeleton className="h-4 w-[120px] mb-2" />
                          <Skeleton className="h-9 w-[70px]" />
                        </div>
                        <Skeleton className="h-14 w-14 rounded-xl" />
                      </div>
                      <div className="pt-3 border-t border-slate-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Skeleton className="w-2 h-2 rounded-full mr-2" />
                            <Skeleton className="h-4 w-[120px]" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* BannerFilters skeleton */}
          <Card className="p-6">
            <div className="flex gap-3 items-center">
              <Skeleton className="h-11 flex-1" />
              <Skeleton className="h-11 w-[120px]" />
              <Skeleton className="h-11 w-[160px]" />
            </div>
          </Card>

          {/* BannerTable skeleton */}
          <Card>
            <div className="overflow-hidden border border-slate-200 rounded-md">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="py-3.5 px-4"><Skeleton className="h-5 w-[150px]" /></TableHead>
                    <TableHead className="py-3.5 px-4"><Skeleton className="h-5 w-[80px]" /></TableHead>
                    <TableHead className="py-3.5 px-4"><Skeleton className="h-5 w-[120px]" /></TableHead>
                    <TableHead className="py-3.5 px-4"><Skeleton className="h-5 w-[100px]" /></TableHead>
                    <TableHead className="py-3.5 px-4 text-right"><Skeleton className="h-5 w-[60px] ml-auto" /></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i} className="hover:bg-slate-50">
                      <TableCell className="py-4 px-4">
                        <div className="space-y-1">
                          <Skeleton className="h-5 w-[200px]" />
                          <Skeleton className="h-4 w-[160px]" />
                        </div>
                      </TableCell>
                      <TableCell className="py-4 px-4"><Skeleton className="h-7 w-[80px] rounded-lg" /></TableCell>
                      <TableCell className="py-4 px-4">
                        <div className="space-y-1">
                          <Skeleton className="h-4 w-[100px]" />
                          <Skeleton className="h-4 w-[100px]" />
                        </div>
                      </TableCell>
                      <TableCell className="py-4 px-4"><Skeleton className="h-7 w-[90px] rounded-lg" /></TableCell>
                      <TableCell className="py-4 px-4 text-right"><Skeleton className="h-9 w-9 rounded-lg ml-auto" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex-1">
      <div className="flex-1 space-y-6 p-8 pt-6">
        {/* Hero Section */}
        <div className="rounded-2xl overflow-hidden shadow-lg">
          <div
            className="flex"
            style={{
              background: 'linear-gradient(to right, #6b21a8, #a855f7)',
            }}
          >
            <div className="p-8 flex justify-between items-start relative w-full">
              <div className="space-y-3 flex-1">
                <div className="text-white/80 text-sm font-medium">
                  Banner Yönetim Paneli
                </div>
                <h1 className="text-3xl font-bold text-white">
                  Banner ve Duyuru Kontrolü
                </h1>
                <p className="text-white/90 text-sm max-w-xl leading-relaxed">
                  Tüm bannerları görüntüleyin, yeni banner oluşturun, mevcut bannerları
                  düzenleyin ve hedef firmalara özel duyurular yapın.
                </p>
              </div>
              <div
                className="w-40 h-40 rounded-lg overflow-hidden relative z-10 flex items-center justify-center ml-8"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }}
              >
                <Megaphone className="w-20 h-20 text-white" strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </div>

        {/* İstatistikler */}
        <BannerStats banners={banners} />

        {/* Filtreler */}
        <BannerFilters
          onSearchChange={debouncedSearch}
          onStatusChange={setFilterStatus}
          onTargetChange={setTargetFilter}
          onDisplayTypeChange={setDisplayTypeFilter}
          statusValue={filterStatus}
          targetValue={targetFilter}
          displayTypeValue={displayTypeFilter}
          onAddBanner={() => navigate('/notices/new')}
        />

        {/* Duyuru Tablosu */}
        {filteredBanners.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 border rounded-lg bg-background">
            <p className="text-lg font-medium mb-2">Duyuru Bulunamadı</p>
            <p className="text-muted-foreground text-center max-w-md">
              {searchQuery || filterStatus !== 'all' || targetFilter !== 'all' || displayTypeFilter !== 'all'
                ? 'Arama kriterlerinize uygun duyuru bulunamadı. Lütfen farklı bir arama yapmayı deneyin.'
                : 'Henüz hiç duyuru eklenmemiş. Yeni bir duyuru eklemek için "Yeni Duyuru Ekle" butonunu kullanın.'}
            </p>
          </div>
        ) : (
          <BannerTable
            banners={filteredBanners}
            onDelete={handleDeleteBanner}
          />
        )}
      </div>
    </div>
  )
}
